import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { personKey, isoToLocalHm, ORIGIN_LABELS, STATUS_LABELS, type PersonRef } from '@/lib/shift-format';
import { DOW_LABELS, dayOfWeekOf, dayTextClass, holidayNameOf, resolveDayKind } from '@/lib/calendar-day';
import { useHolidays } from '@/hooks/useHolidays';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/shift/print` (v0.4 章⑥ F-6、D-78/D-110/§4-11c)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-10 / §4-11c
 *
 * ⚠️ D-173: 印刷は `/shift/periods/:id/print` (期間スコープ) ではなく期間跨ぎの
 * `/shift/print?node_id&from&to&layout=` (routes/shift-misc.ts)。半月編成 (D-16) と月単位の掲示
 * (D-78) が非対称なため、期間に閉じると掲示物が作れない。
 * ⚠️ PDF は生成しない (§8-40、Workers の CPU時間制約に直撃するため)。HTML + `@media print` で組版する。
 * ⚠️ D-110 の時刻非表示モードはここには適用しない (self scope の `/shift/my` 専用の設計判断)。
 *    この画面は `shift:view` (店舗スコープ) が要る管理者/店長向けの画面であり、常に時刻を表示する。
 * ⚠️ 使い勝手フィードバック (2026-09-04、D-U12/D-U13): (a) draft (未確定) の割当は
 *    routes/shift-misc.ts 側で既に除外済みのため、この画面では除外件数の注記のみ表示する。
 *    (b) 表示形式 (パターン名/時刻) は `shift_print_display_mode` (`/settings/shift-patterns`
 *    で設定) をサーバー側が解決し `display_mode`/`pattern_label` として返す。
 */

type PrintAssignment = PersonRef & {
  assignment_id: string;
  person_name: string | null;
  work_date: string;
  start_at: string;
  end_at: string;
  origin: string;
  status: string;
  pattern_label: string | null;
};

type PrintDisplayMode = 'pattern_name' | 'time';

type PrintResponse = {
  node_id: string; from: string; to: string; layout: 'calendar' | 'matrix';
  display_mode: PrintDisplayMode;
  excluded_draft_count: number;
  assignments: PrintAssignment[];
};

type Layout = 'calendar' | 'matrix';

const toDateOnly = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (yyyyMmDd: string, days: number): string => {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d, 0, 0, 0, 0);
  dt.setDate(dt.getDate() + days);
  return toDateOnly(dt);
};
const buildDateList = (from: string, to: string): string[] => {
  const out: string[] = [];
  for (let cur = from; cur <= to; cur = addDays(cur, 1)) out.push(cur);
  return out;
};
const dayOfMonthLabel = (yyyyMmDd: string): string => String(Number(yyyyMmDd.slice(8, 10)));

// ⚠️ WCAG 1.4.1 (色だけで区別しない): origin/status は文言主体で表現する。ここでの短縮記号は
// 表示の圧縮用であり、正式なラベルは lib/shift-format.ts の ORIGIN_LABELS/STATUS_LABELS を単一の
// 参照元とする (紙面の凡例もそこから生成する)。
const ORIGIN_MARKS: Record<string, string> = { standing_pattern: '定', manual: '調', preference_based: '希', auto_generated: 'AI' };
const originMark = (origin: string): string => ORIGIN_MARKS[origin] ?? '?';

/** A3 での用紙サイズ・余白。カレンダー型=縦、マトリクス型=横 (D-78)。
 *  ⚠️ AppLayout.tsx のヘッダー/サイドバー/スクロールコンテナは編集対象外のため、既存のクラス名
 *  (header.bg-chrome-base / aside.bg-chrome-base / 外枠の overflow-hidden コンテナ) を印刷時のみ
 *  CSSで無効化する。AppLayout.tsx のクラス構成が変わった場合はここも見直しが必要 (既存に
 *  `@media print` の共通規約が無いため、本画面固有の対応として一時的にここへ置く)。 */
function PrintStyles({ layout }: { layout: Layout }) {
  return (
    <style>{`
      @media print {
        @page { size: A3 ${layout === 'matrix' ? 'landscape' : 'portrait'}; margin: 10mm; }
        div.overflow-hidden.bg-chrome-base, main.overflow-y-auto { height: auto !important; overflow: visible !important; }
        header.bg-chrome-base, aside.bg-chrome-base { display: none !important; }
        /* design-expertレビュー反映: chrome非表示だけでは main 内側パネルの余白/最小高さ
           (AppLayout.tsx の p-2/p-4等・min-h-[calc(...)]) がA3用紙の先頭に無駄な余白を作る */
        main.overflow-y-auto > div { padding: 0 !important; min-height: 0 !important; }
        main.overflow-y-auto > div > div { box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; min-height: 0 !important; }
        .print-calendar-week { break-inside: avoid; }
        .print-matrix-block { break-inside: auto; }
        .print-matrix-block tr { break-inside: avoid; }
        .print-matrix-block thead { display: table-header-group; }
        /* D-234 §1-2(b): モノクロ印刷で色が消えても判別できるよう、祝日セルに網掛けを併用する。
           print-color-adjust が無いとブラウザの「背景グラフィックを印刷」設定オフ時に消える。 */
        .print-holiday-hatch {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
          background-image: repeating-linear-gradient(45deg, rgba(179,38,30,0.14) 0, rgba(179,38,30,0.14) 2px, transparent 2px, transparent 6px);
        }
      }
    `}
    </style>
  );
}

function NodeSelector({ nodeId, onChange }: { nodeId: string; onChange: (id: string) => void }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">対象店舗</label>
      <Select value={nodeId || undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="店舗を選ぶ" /></SelectTrigger>
        <SelectContent>
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function OriginLegend() {
  return (
    <p className="text-[11px] text-text-sub mt-2">
      凡例:{' '}
      {(Object.keys(ORIGIN_MARKS) as (keyof typeof ORIGIN_MARKS)[]).map((k, i) => (
        <span key={k}>{i > 0 && ' ・ '}{ORIGIN_MARKS[k]}={ORIGIN_LABELS[k] ?? k}</span>
      ))}
    </p>
  );
}

function AssignmentLine({ a, displayMode }: { a: PrintAssignment; displayMode: PrintDisplayMode }) {
  // D-U13: パターン名表示モードでも、パターン未設定の割当は時刻表示にフォールバックする
  // (「無言の欠落」を避け、枠自体は必ず何かを表示する)。
  const showsTime = displayMode === 'time' || !a.pattern_label;
  return (
    <div className="text-[10px] leading-tight text-text-body">
      {showsTime
        ? <span className="tabular-nums">{isoToLocalHm(a.start_at)}〜{isoToLocalHm(a.end_at)}</span>
        // design-expertレビュー反映 (2026-09-04): パターン名は自由記述 (最大200字) で
        // 区切り文字を含まない場合があり、break-words 無しだと狭いセル (マトリクス型の日付列) を
        // 突き破って崩れる (時刻表示は固定長のため既存は無指定でも問題なかった)。
        : <span className="break-words">{a.pattern_label}</span>}{' '}
      {a.person_name ?? '(不明)'}
      <span className="text-text-sub"> [{originMark(a.origin)}{a.status !== 'confirmed' ? `・${STATUS_LABELS[a.status] ?? a.status}` : ''}]</span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// カレンダー型 (D-78: セルは可変件数、2枠固定にしない、D-36)
// -----------------------------------------------------------------------------
function CalendarLayout({
  from, to, assignments, holidays, displayMode,
}: { from: string; to: string; assignments: PrintAssignment[]; holidays: ReadonlyMap<string, string>; displayMode: PrintDisplayMode }) {
  const weeks = useMemo(() => {
    const start = addDays(from, -dayOfWeekOf(from));
    const end = addDays(to, 6 - dayOfWeekOf(to));
    const out: string[][] = [];
    for (let wStart = start; wStart <= end; wStart = addDays(wStart, 7)) {
      out.push(Array.from({ length: 7 }, (_, i) => addDays(wStart, i)));
    }
    return out;
  }, [from, to]);

  const byDate = useMemo(() => {
    const map = new Map<string, PrintAssignment[]>();
    for (const a of assignments) {
      const list = map.get(a.work_date);
      if (list) list.push(a); else map.set(a.work_date, [a]);
    }
    for (const list of map.values()) list.sort((a, b) => a.start_at.localeCompare(b.start_at));
    return map;
  }, [assignments]);

  return (
    <div className="space-y-0">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {DOW_LABELS.map((l, i) => (
              <th key={l} scope="col" className={`border border-surface-border bg-surface-sunken text-xs font-semibold p-1 w-[14.28%] ${i === 0 ? 'text-calendar-sunday' : i === 6 ? 'text-calendar-saturday' : 'text-text-body'}`}>{l}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]} className="print-calendar-week align-top">
              {week.map((date) => {
                const inRange = date >= from && date <= to;
                const dayAssignments = byDate.get(date) ?? [];
                const kind = resolveDayKind(date, holidays);
                const holidayName = holidayNameOf(date, holidays);
                return (
                  <td
                    key={date}
                    className={`border border-surface-border p-1 align-top h-24 ${inRange ? '' : 'bg-surface-sunken/40'} ${inRange && kind === 'holiday' ? 'print-holiday-hatch' : ''}`}
                  >
                    <div className={`text-[11px] font-semibold ${inRange ? (dayTextClass(kind) || 'text-text-sub') : 'text-text-sub'}`}>
                      {inRange ? dayOfMonthLabel(date) : ''}
                      {inRange && holidayName && <span aria-hidden="true">・</span>}
                    </div>
                    {inRange && holidayName && <div className="sr-only">（{holidayName}）</div>}
                    {inRange && (
                      <div className="space-y-0.5 mt-0.5">
                        {dayAssignments.map((a) => <AssignmentLine key={a.assignment_id} a={a} displayMode={displayMode} />)}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <OriginLegend />
    </div>
  );
}

// -----------------------------------------------------------------------------
// マトリクス型 (店舗×月、行=従業員・列=日)
// ⚠️ CSS には「表の列を次ページへ折り返す」機能が無いため、日付を CHUNK_SIZE 列単位に分割して
// 複数の <table> を積む (各 table は thead を repeat し、行 (人) 単位で改ページする)。
// -----------------------------------------------------------------------------
const MATRIX_CHUNK_SIZE = 14;

function MatrixLayout({
  from, to, assignments, holidays, displayMode,
}: { from: string; to: string; assignments: PrintAssignment[]; holidays: ReadonlyMap<string, string>; displayMode: PrintDisplayMode }) {
  const dates = useMemo(() => buildDateList(from, to), [from, to]);
  const chunks = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < dates.length; i += MATRIX_CHUNK_SIZE) out.push(dates.slice(i, i + MATRIX_CHUNK_SIZE));
    return out;
  }, [dates]);

  const rows = useMemo(() => {
    const map = new Map<string, { key: string; name: string; byDate: Map<string, PrintAssignment[]> }>();
    for (const a of assignments) {
      const key = personKey(a);
      let row = map.get(key);
      if (!row) { row = { key, name: a.person_name ?? '(不明)', byDate: new Map() }; map.set(key, row); }
      const list = row.byDate.get(a.work_date);
      if (list) list.push(a); else row.byDate.set(a.work_date, [a]);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'ja'));
  }, [assignments]);

  return (
    <div className="space-y-4">
      {chunks.map((chunk) => (
        <table key={chunk[0]} className="print-matrix-block w-full border-collapse">
          <thead>
            <tr>
              <th scope="col" className="border border-surface-border bg-surface-sunken text-xs font-semibold text-text-body p-1 text-left w-32">氏名</th>
              {chunk.map((date) => {
                const kind = resolveDayKind(date, holidays);
                const holidayName = holidayNameOf(date, holidays);
                return (
                  <th
                    key={date} scope="col"
                    className={`border border-surface-border bg-surface-sunken text-[10px] font-semibold p-1 w-[5%] ${dayTextClass(kind) || 'text-text-body'} ${kind === 'holiday' ? 'print-holiday-hatch' : ''}`}
                  >
                    {dayOfMonthLabel(date)}<br />({DOW_LABELS[dayOfWeekOf(date)]})
                    {holidayName && <span aria-hidden="true">・</span>}
                    {holidayName && <span className="sr-only">（{holidayName}）</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row" className="border border-surface-border p-1 text-xs font-semibold text-text-body text-left whitespace-nowrap">{row.name}</th>
                {chunk.map((date) => {
                  const dayAssignments = row.byDate.get(date) ?? [];
                  return (
                    <td key={date} className="border border-surface-border p-0.5 align-top">
                      {dayAssignments.map((a) => <AssignmentLine key={a.assignment_id} a={a} displayMode={displayMode} />)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ))}
      <OriginLegend />
    </div>
  );
}

// -----------------------------------------------------------------------------
export function ShiftPrintPage() {
  const [nodeId, setNodeId] = useState('');
  const [layout, setLayout] = useState<Layout>('calendar');
  const [from, setFrom] = useState(() => { const d = new Date(); return toDateOnly(new Date(d.getFullYear(), d.getMonth(), 1)); });
  const [to, setTo] = useState(() => { const d = new Date(); return toDateOnly(new Date(d.getFullYear(), d.getMonth() + 1, 0)); });
  const holidays = useHolidays(from, to, nodeId);

  const { data, isLoading, isError, error } = useQuery<PrintResponse>({
    queryKey: ['shift-print', nodeId, from, to, layout],
    queryFn: () => apiFetch<PrintResponse>(`/shift/print?node_id=${encodeURIComponent(nodeId)}&from=${from}&to=${to}&layout=${layout}`),
    enabled: !!nodeId && !!from && !!to && from <= to,
  });

  const errorMessage = error instanceof ApiClientError
    ? (error.status === 403 ? 'この店舗のシフト印刷データを閲覧する権限がありません。' : error.status === 404 ? '指定した店舗が見つかりません。' : error.message)
    : 'シフト印刷データの取得に失敗しました。';

  return (
    <div className="space-y-4">
      <PrintStyles layout={layout} />

      <div className="print:hidden">
        <h2 className="text-h2">シフト印刷</h2>
        <p className="text-sm text-text-sub mt-1">
          店舗と期間を指定して、掲示用のシフト表を印刷します (カレンダー型はA3縦・マトリクス型はA3横)。
          給与・単価は含まれません。
        </p>
      </div>

      <div className="print:hidden flex flex-wrap items-end gap-3">
        <NodeSelector nodeId={nodeId} onChange={setNodeId} />
        <div>
          <label htmlFor="shift-print-from" className="block text-xs text-text-sub mb-1">期間 (開始)</label>
          <input id="shift-print-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="min-h-[44px] px-3 py-1.5 border border-surface-border rounded text-sm bg-surface-card" />
        </div>
        <div>
          <label htmlFor="shift-print-to" className="block text-xs text-text-sub mb-1">期間 (終了)</label>
          <input id="shift-print-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="min-h-[44px] px-3 py-1.5 border border-surface-border rounded text-sm bg-surface-card" />
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">レイアウト</label>
          <div className="flex rounded-full border border-surface-border overflow-hidden" role="group" aria-label="レイアウト">
            {(['calendar', 'matrix'] as const).map((l) => (
              <button key={l} type="button" aria-pressed={layout === l} onClick={() => setLayout(l)}
                className={`px-3 min-h-[44px] text-xs font-medium ${layout === l ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {l === 'calendar' ? 'カレンダー型' : 'マトリクス型'}
              </button>
            ))}
          </div>
        </div>
        <Button variant="primary" leftIcon={<Printer size={16} />} disabled={!data || isLoading} onClick={() => window.print()}>
          印刷する
        </Button>
      </div>
      {from > to && <p className="print:hidden text-xs text-danger-text">期間 (開始) は期間 (終了) 以前を指定してください。</p>}

      {/* D-U12: draft (未確定) の割当は印刷対象から除外しているため、無言の欠落にならないよう
          除外件数を必ず明示する (印刷面には出さない、確認画面のみの注記)。 */}
      {!!data && data.excluded_draft_count > 0 && (
        <p className="print:hidden text-xs text-text-sub bg-surface-sunken border border-surface-border rounded-md px-3 py-2">
          この期間には未確定の勤務予定が{data.excluded_draft_count}件あり、印刷対象から除外されています。
        </p>
      )}

      {!nodeId ? (
        <p className="print:hidden text-sm text-text-sub">対象店舗を選んでください。</p>
      ) : isLoading ? (
        <div className="print:hidden text-text-sub">読み込み中…</div>
      ) : isError ? (
        <div className="print:hidden p-6 border border-surface-border rounded-lg bg-white text-danger-text">{errorMessage}</div>
      ) : (data?.assignments.length ?? 0) === 0 ? (
        <div className="print:hidden">
          <EmptyState
            message="この期間に印刷できる割当がありません"
            description={
              data && data.excluded_draft_count > 0
                ? '未確定の勤務予定のみのため、印刷対象がありません。シフトを確定してから印刷してください。'
                : '店舗または期間を変更してください。'
            }
          />
        </div>
      ) : (
        <div className="border border-surface-border rounded-lg bg-white p-3 sm:p-4 print:border-0 print:p-0 print:rounded-none overflow-x-auto">
          <div className="hidden print:block mb-2 text-sm font-semibold text-text-body">
            {from} 〜 {to} ・ {layout === 'calendar' ? 'カレンダー型' : 'マトリクス型'}
          </div>
          {layout === 'calendar'
            ? <CalendarLayout from={from} to={to} assignments={data!.assignments} holidays={holidays} displayMode={data!.display_mode} />
            : <MatrixLayout from={from} to={to} assignments={data!.assignments} holidays={holidays} displayMode={data!.display_mode} />}
        </div>
      )}
    </div>
  );
}
